import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { debounce } from '../../utils/helpers';
import localeConfig from '../../config/locale.config';

const MAPS_COUNTRY_CODES =
  Array.isArray(localeConfig.mapsCountryCodes) && localeConfig.mapsCountryCodes.length > 0
    ? localeConfig.mapsCountryCodes
    : ['nz'];

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onAddressSelect, 
  placeholder = "Search for address...",
  disabled = false 
}) => {
  const inputRef = useRef(null);
  const eventListenersRef = useRef([]);
  const debouncedSearchRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showCustomResults, setShowCustomResults] = useState(false);
  const { isLoaded: googleMapsLoaded, loading: googleMapsLoading, error: googleMapsError } = useGoogleMaps();

  // Initialize autocomplete when Google Maps is loaded
  useEffect(() => {
    if (googleMapsLoaded && inputRef.current) {
      // Don't initialize Google Autocomplete widget - we'll use custom search only
    }

    const inputEl = inputRef.current;
    // Cleanup function to remove event listeners
    return () => {
      if (inputEl && eventListenersRef.current) {
        eventListenersRef.current.forEach(({ type, listener }) => {
          inputEl.removeEventListener(type, listener);
        });
        eventListenersRef.current = [];
      }
    };
  }, [googleMapsLoaded, googleMapsLoading, googleMapsError]);

  // Removed initializeAutocomplete function - using custom debounced search only

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    if (onChange) {
      onChange(newValue);
    }

    // Clear existing debounced search
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current.cancel();
    }

    // Hide custom results when user is typing
    setShowCustomResults(false);
    setSearchResults([]);

    // Create new debounced search
    debouncedSearchRef.current = debounce(() => {
      if (newValue.length > 2) {
        performDebouncedSearch(newValue);
      }
    }, 300);

    // Execute debounced search
    debouncedSearchRef.current();
  };

  const performDebouncedSearch = (query) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    setIsLoading(true);
    setShowCustomResults(true);

    try {
      // Use the new Places API with AutocompleteService
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      
      const request = {
        input: query,
        // Single code or up to 5 ISO country codes from locale config
        componentRestrictions: {
          country: MAPS_COUNTRY_CODES.length === 1 ? MAPS_COUNTRY_CODES[0] : MAPS_COUNTRY_CODES,
        },
        types: ['establishment', 'geocode'], // Include businesses and addresses
      };

      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          // Convert predictions to search results format
          const searchResults = predictions.slice(0, 5).map(prediction => ({
            place_id: prediction.place_id,
            name: prediction.structured_formatting?.main_text || prediction.description,
            formatted_address: prediction.description,
            geometry: null // We'll get this when user selects
          }));
          
          setSearchResults(searchResults);
        } else {
          setSearchResults([]);
          setShowCustomResults(false);
        }
      });
    } catch (error) {
      setIsLoading(false);
      setSearchResults([]);
      setShowCustomResults(false);
    }
  };

  const handleSearchResultSelect = (place) => {
    // Get full place details using PlacesService
    if (place.place_id) {
      const mapElement = document.createElement('div');
      mapElement.style.display = 'none';
      document.body.appendChild(mapElement);
      
      const map = new window.google.maps.Map(mapElement);
      const service = new window.google.maps.places.PlacesService(map);
      
      const request = {
        placeId: place.place_id,
        fields: ['address_components', 'formatted_address', 'geometry', 'name']
      };

      service.getDetails(request, (placeDetails, status) => {
        // Clean up the map element
        document.body.removeChild(mapElement);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && placeDetails) {
          // Extract address components
          const addressComponents = placeDetails.address_components || [];
          let locality = '';
          let administrativeAreaLevel1 = '';
          let administrativeAreaLevel2 = '';
          let postalCode = '';

          addressComponents.forEach(component => {
            const types = component.types;
            
            if (types.includes('locality')) {
              locality = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              administrativeAreaLevel1 = component.long_name;
            } else if (types.includes('administrative_area_level_2')) {
              administrativeAreaLevel2 = component.long_name;
            } else if (types.includes('postal_code')) {
              postalCode = component.long_name;
            }
          });

          // Prepare address data
          const addressData = {
            address: placeDetails.formatted_address || placeDetails.name,
            state: administrativeAreaLevel1,
            city: locality || administrativeAreaLevel2,
            latitude: placeDetails.geometry?.location?.lat() || null,
            longitude: placeDetails.geometry?.location?.lng() || null,
            postalCode: postalCode
          };

          if (onAddressSelect) {
            onAddressSelect(addressData);
          }
        } else {
          // Fallback to basic data
          const addressData = {
            address: place.formatted_address || place.name,
            state: '',
            city: '',
            latitude: null,
            longitude: null,
            postalCode: ''
          };
          
          if (onAddressSelect) {
            onAddressSelect(addressData);
          }
        }
        
        setShowCustomResults(false);
        setSearchResults([]);
      });
    } else {
      // Fallback if no place_id
      const addressData = {
        address: place.formatted_address || place.name,
        state: '',
        city: '',
        latitude: null,
        longitude: null,
        postalCode: ''
      };
      
      if (onAddressSelect) {
        onAddressSelect(addressData);
      }
      
      setShowCustomResults(false);
      setSearchResults([]);
    }
  };

  const handleKeyDown = (e) => {
    // Handle keyboard navigation when custom search results are open
    if (showCustomResults && searchResults.length > 0) {
      switch (e.key) {
        case 'Enter':
          // Prevent Enter key from bubbling up to form
          e.preventDefault();
          e.stopPropagation();
          // Select first result if available
          if (searchResults.length > 0) {
            handleSearchResultSelect(searchResults[0]);
          }
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          // Allow navigation but don't bubble to form
          e.stopPropagation();
          break;
        case 'Escape':
          // Close dropdown and don't bubble to form
          setShowCustomResults(false);
          setSearchResults([]);
          e.stopPropagation();
          break;
        default:
          // Other keys are fine to bubble
          break;
      }
    }
  };

  // Show loading state while Google Maps is loading
  if (googleMapsLoading) {
    return (
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder="Loading Google Maps..."
          disabled={true}
          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Show error state if Google Maps failed to load
  if (googleMapsError) {
    return (
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder="Google Maps not available"
          disabled={true}
          className="w-full px-3 py-2 border border-red-300 rounded-md bg-red-50 text-red-600"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled || !googleMapsLoaded}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-600"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      )}
      
      
      {/* Custom Search Results Dropdown */}
      {showCustomResults && searchResults.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {searchResults.map((place, index) => (
            <div
              key={place.place_id || index}
              className="px-3 py-1.5 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm"
              onClick={() => handleSearchResultSelect(place)}
            >
              <div className="truncate">
                <span className="font-bold">{place.name}</span>
                <span className="text-gray-600">, {place.formatted_address}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete; 